import React, { useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { getDefaultMessageResultsFound, getTranslateByKey } from "@/main/services/utils";
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { toast } from "react-toastify";
import * as S from "./styles";
import { InsurerFilterRequest } from "@/main/models/request";
import { CompanyTypeEnum } from "@/main/services/enum/CompanyTypeEnum";
import { insurerApi, valuatorApi } from "@/main/api";
import { SaveInsurerCompanyByValuator } from "@/main/models/request/valuatorRequest";
import KogInputText from "../../inputText/inputText";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { IGetByInsurer } from "@/main/models/response";
import KOGCollapse from "../../kogCollapse/kogCollapse";
import KOGInsurerCompanyBoxGridMultiCheckbox from "../insurerCompanyBoxGridMultiCheckbox/insurerCompanyBoxGridMultiCheckbox";
import { useUserReducer } from "@/main/store/user";
import useAsyncModalConfirmReason from "../../asyncModalConfirmReason/asyncModalConfirmReason";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";

interface Props {
	open?: boolean;
	closeModal: () => void;
	guidValuator: string | null;
	loadGridList: () => void;
}

function InsurerCompanySidebar({ open, closeModal, guidValuator, loadGridList }: Props) {
	const INITIAL_FORM_STATE_SEARCH = {
		companyName: "",
	};

	type IInsurerCompanyFormSearch = typeof INITIAL_FORM_STATE_SEARCH;

	const {
		setValue,
		reset,
		register,
		setError,
		clearErrors,
		watch,
		formState: { errors },
	} = useForm<IInsurerCompanyFormSearch>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE_SEARCH,
	});

	const [isLoading, setIsLoading] = useState(false);
	const [insurers, setInsurers] = useState<IGetByInsurer[]>([]);
	const [totalItems, setTotalItems] = useState(0);

	const { currentUser } = useUserReducer();
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();

	const titleGrid = getDefaultMessageResultsFound(insurers.length, totalItems);

	const searchInsurers = useCallback(
		async (pageIndex: number = 1) => {
			const guidCompany = currentUser?.idCompanyType === CompanyTypeEnum.Insurer ? currentUser?.guidCompany : null;

			const request: InsurerFilterRequest = {
				companyName: !guidCompany ? watch("companyName") : null,
				guidCompany: guidCompany,
				pageIndex: pageIndex,
				pageSize: PaginationEnum.PageSize15,
			};

			try {
				const response = await insurerApi.getByInsurer(request);
				const { results, totalNumberOfRecords } = response.data;

				if (pageIndex === PaginationEnum.PageIndexIntial) {
					setInsurers(results);
				} else {
					setInsurers((prev) => [...prev, ...results]);
				}

				setTotalItems(totalNumberOfRecords);
				return results;
			} catch {
				return [];
			}
		},
		[currentUser, watch],
	);

	function onSearch() {
		const companyName = watch("companyName");
		if (!companyName || companyName.trim() === "") {
			setError("companyName", {
				type: "manual",
				message: getTranslateByKey("information.insurerRequired", true),
			});
			return;
		}
		clearErrors("companyName");
		setIsLoading(true);

		searchInsurers(PaginationEnum.PageIndexIntial).finally(() => {
			setIsLoading(false);
		});
	}

	const handleLoadMore = useCallback(
		async (pageIndex: number) => {
			const results = await searchInsurers(pageIndex);
			return results;
		},
		[searchInsurers],
	);

	async function onSubmitSave(request: SaveInsurerCompanyByValuator) {
		let filesRequest = undefined;
		let guidReason = null;
		let reasonDescription = null;

		if (guidValuator) {
			const resultConfirm = await getConfirmationReason(
				getTranslateByKey("information.confirmChangeTitle"),
				ReasonTypeEnum.ChangeCompany,
			);

			if (!resultConfirm.confirmed) return;

			if (resultConfirm.files && resultConfirm.files.length > 0) {
				filesRequest = await Promise.all(
					resultConfirm.files.map(async (item) => {
						const base64DataUrl = await toBase64(item);
						return {
							fileName: item.name,
							contentType: item.type,
							fileBase64: base64DataUrl as string,
						};
					}),
				);
			}

			guidReason = resultConfirm.reason ? String(resultConfirm.reason.key) : null;
			reasonDescription = resultConfirm.observation || null;
		}

		setIsLoading(true);

		const newRequest: SaveInsurerCompanyByValuator = {
			guidValuator: guidValuator as string,
			guidInsurer: request.guidInsurer,
			changeJustification: {
				guidReason,
				reasonDescription,
				filesUpload: filesRequest,
			},
		};

		valuatorApi
			.saveInsurerCompanyByValuator(newRequest)
			.then(() => {
				toast.success(getTranslateByKey("information.valuatorSavedSuccessfully"));
				closeModal();
				handleClear();
				loadGridList();
			})
			.catch(() => {})
			.finally(() => {
				setIsLoading(false);
			});
	}

	function handleBack() {
		handleClear();
	}

	function handleClear() {
		reset(INITIAL_FORM_STATE_SEARCH);
		setValue("companyName", "");
		setInsurers([]);
		setTotalItems(0);
	}

	const rowHeaders = [getTranslateByKey("information.insurer")];

	return (
		<>
			{open && (
				<KogModalSideComponent
					open={open}
					closeModal={() => closeModal()}
					id="id-blacklist-modal-scroll"
					titleModalReducer={getTranslateByKey("information.registerInsurerScreen")}
					title={<S.Title>{getTranslateByKey("information.registerInsurerScreen")}</S.Title>}
				>
					<S.Container>
						<KogInputText
							id="id-insurer-name-input"
							label={`${getTranslateByKey("information.insurer")}`}
							name="companyName"
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								setValue("companyName", e.target.value);
								if (e.target.value) clearErrors("companyName");
							}}
							onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
								if (e.key === "Enter") {
									e.preventDefault();
									onSearch();
								}
							}}
							required
							error={!!errors.companyName}
							helperText={errors.companyName?.message}
							inputRef={register("companyName").ref}
						/>
						<S.AreaButtons>
							<SecondaryButton id="id-holiday-btn-back" onClick={handleBack}>
								{getTranslateByKey("information.clear")}
							</SecondaryButton>
							<SecondaryButton id="id-InsurerCompanyRegister-button-search" onClick={onSearch} isLoading={isLoading}>
								{getTranslateByKey("information.search")}
							</SecondaryButton>
						</S.AreaButtons>
					</S.Container>

					{insurers.length > 0 && (
						<S.Container style={{ marginTop: 20 }}>
							<KOGCollapse
								showBorder={false}
								header={
									<S.BodyBox style={{ position: "initial" }}>
										<S.Title>{getTranslateByKey("information.register")}</S.Title>
									</S.BodyBox>
								}
								content={
									<div style={{ paddingTop: 16 }}>
										<S.Title style={{ paddingLeft: 8, fontSize: 14 }}>{titleGrid}</S.Title>
										<KOGInsurerCompanyBoxGridMultiCheckbox
											rowHeaders={rowHeaders}
											dataInsurersCompany={insurers}
											onSubmitSave={onSubmitSave}
											isLoading={isLoading}
											onLoadMore={handleLoadMore}
										/>
									</div>
								}
								isOpenCollapseinitial={true}
							/>
						</S.Container>
					)}
					<ModalConfirmationReason />
				</KogModalSideComponent>
			)}
		</>
	);
}

export default InsurerCompanySidebar;
